import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { BOAPI } from '../service/UsersService';
import utils from '../utils/utils';
import BusinessEditModal from './BusinessEditModal';
import ImportBusinessesModal from './ImportBusinessesModal';

// תמונה יכולה להיות קישור חיצוני מלא (http/https, למשל ייבוא סקרייפ) ולא
// רק נתיב /uploads/... מקומי - ראו הערה זהה ב-BusinessesTab/BusinessEditModal
const fullUrl = (path) => (!path ? null : /^https?:\/\//i.test(path) ? path : `${utils.BaseUrl}${path}`);

const DAY_LABELS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

// openingHours נשמר כטקסט JSON (DayHours[], day 0=ראשון...6=שבת) - ראו
// הערה מלאה ב-Tables.sql/BusinessEditModal.jsx. כאן רק תצוגה לקריאה בלבד
function formatOpeningHours(raw) {
  if (!raw) return null;
  try {
    const days = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(days) || days.length === 0) return null;
    const openDays = days.filter((d) => d.isOpen);
    if (openDays.length === 0) return 'סגור בכל הימים';
    return openDays.map((d) => `${DAY_LABELS[d.day] || d.day}: ${d.open}-${d.close}`).join(' | ');
  } catch {
    return null;
  }
}

const STATUS_TABS = [
  { key: 'pending', label: 'ממתינות לאישור' },
  { key: 'approved', label: 'אושרו' },
  { key: 'rejected', label: 'נדחו' },
  { key: '', label: 'הכל' },
];

const STATUS_BADGE = {
  pending: { text: 'ממתין לאישור', style: { background: '#FEF9EE', color: '#8C5A0C' } },
  approved: { text: 'אושר', style: { background: '#e8f5e9', color: '#2e7d32' } },
  rejected: { text: 'נדחה', style: { background: '#ffebee', color: '#c62828' } },
};

// בתי עסק שהוגשו דרך האפליקציה (submitBusiness.tsx, spSubmitBusiness) -
// לא לבלבל עם טאב "בתי עסק" (שם רק בתי עסק שכבר אושרו). אישור מעתיק
// בפועל לטבלת Businesses (spBOApproveBusinessSubmission) - עד לרגע הזה
// בית העסק לא מוצג במאגר הציבורי בכלל, ואוטומטית הופך את המגיש ל-owner
const BusinessSubmissionsTab = () => {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    loadSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const resp = await BOAPI.fetchBusinessSubmissions(statusFilter || undefined);
      if (resp?.[0]?.[0]?.code === 0) setSubmissions(resp[1] || []);
      else setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת הבקשות');
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  // אישור = פתיחת טופס בית העסק המלא, ממולא בפרטי ההצעה, כדי להשלים את מה
  // שחסר בה (עמלת סליקה - חובה, מוצרים, גלריה, נ"צ, סדר תצוגה וכו'). בית העסק
  // נוצר ומופעל רק בשמירת הטופס (BusinessEditModal במצב submissionId)
  const [approvingSubmission, setApprovingSubmission] = useState(null);

  const approve = (submission) => {
    setError('');
    setApprovingSubmission(submission);
  };

  const submissionAsBusiness = (sub) => ({
    name: sub.name,
    category: sub.category,
    categories: sub.categories,
    description: sub.description,
    logoUrl: sub.logoUrl,
    address: sub.address,
    city: sub.city,
    lat: sub.lat,
    lng: sub.lng,
    phone: sub.phone || sub.contactPhone || '',
    discountPercent: sub.discountPercent,
    commissionPercent: '',
    benefitDescription: sub.benefitDescription,
    openingHours: sub.openingHours,
    ownerId: sub.ownerId,
    ownerName: sub.ownerName,
    // בעל העסק כבר ידוע (מגיש ההצעה) - העסק עולה לאוויר מיד באישור
    ownerConfirmedAt: new Date().toISOString(),
    leadStatus: 'confirmed',
  });

  const reject = async (submission) => {
    setBusyId(submission.id);
    setError('');
    try {
      const resp = await BOAPI.rejectBusinessSubmission(submission.id, rejectReason.trim() || undefined);
      if (resp?.[0]?.[0]?.code === 0) {
        showSuccess(`"${submission.name}" נדחה`);
        setSubmissions((prev) => prev.filter((s) => s.id !== submission.id));
        setRejectingId(null);
        setRejectReason('');
      } else {
        setError(resp?.[0]?.[0]?.msg || 'הדחייה נכשלה');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setBusyId(null);
    }
  };

  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '-';

  // מייצא את הרשימה המסוננת כרגע (לפי טאב הסטטוס) - אותו עיקרון בדיוק כמו
  // הייצוא בטאב "בתי עסק" (BusinessesTab.jsx)
  const exportToExcel = () => {
    const data = submissions.map((sub) => ({
      'שם': sub.name,
      'סטטוס': (STATUS_BADGE[sub.status] || STATUS_BADGE.pending).text,
      'קטגוריה': sub.categoryLabel || sub.category,
      'אחוז הנחה': sub.discountPercent,
      'פירוט ההטבה': sub.benefitDescription || '',
      'תיאור': sub.description || '',
      'עיר': sub.city || '',
      'כתובת': sub.address || '',
      'טלפון בית העסק': sub.phone || '',
      'שעות פתיחה': formatOpeningHours(sub.openingHours) || '',
      'איש קשר לחזרה': sub.contactName || '',
      'טלפון ליצירת קשר': sub.contactPhone || '',
      'בעל החשבון': sub.ownerName || '',
      'אימייל בעל החשבון': sub.ownerEmail || '',
      'סיבת דחייה': sub.rejectionReason || '',
      'תאריך הגשה': fmtDate(sub.iTime),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = Object.keys(data[0] || {}).map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'הצעות בתי עסק');
    XLSX.writeFile(wb, `הצעות-בתי-עסק-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div style={s.container}>
      {approvingSubmission && (
        <BusinessEditModal
          business={submissionAsBusiness(approvingSubmission)}
          submissionId={approvingSubmission.id}
          onClose={() => setApprovingSubmission(null)}
          onSaved={() => {
            const approved = approvingSubmission;
            setApprovingSubmission(null);
            showSuccess(`"${approved.name}" אושר ונוסף לבתי העסק כבית עסק פעיל`);
            setSubmissions((prev) => prev.filter((x) => x.id !== approved.id));
          }}
        />
      )}
      <div style={s.headerRow}>
        <h2 style={s.title}>הצעות בתי עסק</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={s.totalBadge}>{submissions.length}</span>
          <button style={s.exportBtn} onClick={exportToExcel} disabled={submissions.length === 0}>
            ⬇ ייצוא לאקסל
          </button>
          <button style={s.importBtn} onClick={() => setImporting(true)}>📥 ייבוא מאקסל</button>
        </div>
      </div>

      <div style={s.tabsRow}>
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            style={{ ...s.tabBtn, ...(statusFilter === t.key ? s.tabBtnActive : {}) }}
            onClick={() => setStatusFilter(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError('')}>✕</button>
        </div>
      )}
      {successMsg && <div style={s.successBanner}>✅ {successMsg}</div>}

      {loading ? (
        <div style={s.center}>טוען...</div>
      ) : submissions.length === 0 ? (
        <div style={s.emptyState}>
          <div style={{ fontSize: 48 }}>🏪</div>
          <p style={{ color: '#697386', fontWeight: 600 }}>אין בקשות להצגה</p>
        </div>
      ) : (
        submissions.map((sub) => {
          const isBusy = busyId === sub.id;
          const badge = STATUS_BADGE[sub.status] || STATUS_BADGE.pending;
          return (
            <div key={sub.id} style={s.card}>
              <div style={s.cardHeaderRow}>
                <span style={{ ...s.statusBadge, ...badge.style }}>{badge.text}</span>
                <span style={s.businessName}>{sub.name}</span>
                <span style={s.dateText}>{fmtDate(sub.iTime)}</span>
              </div>

              <div style={s.cardBody}>
                {sub.logoUrl && <img src={fullUrl(sub.logoUrl)} alt="" style={s.thumb} />}
                <div style={s.detailsCol}>
                  <DetailRow label="קטגוריה" value={sub.categoryLabel || sub.category} />
                  <DetailRow label="📞 איש קשר לחזרה" value={`${sub.contactName} · ${sub.contactPhone || 'אין טלפון'}`} strong />
                  <DetailRow label="בעל החשבון" value={`${sub.ownerName}${sub.ownerEmail ? ` · ${sub.ownerEmail}` : ''}`} />
                  <DetailRow label="הנחה מוצעת" value={`${sub.discountPercent}%`} />
                  {sub.benefitDescription && <DetailRow label="פירוט ההטבה" value={sub.benefitDescription} />}
                  <DetailRow label="כתובת" value={[sub.city, sub.address].filter(Boolean).join(', ') || '-'} />
                  {sub.phone && <DetailRow label="📞 טלפון בית העסק" value={sub.phone} />}
                  {formatOpeningHours(sub.openingHours) && <DetailRow label="🕐 שעות פתיחה" value={formatOpeningHours(sub.openingHours)} />}
                  {sub.description && <DetailRow label="תיאור" value={sub.description} />}
                  {sub.status === 'rejected' && sub.rejectionReason && (
                    <DetailRow label="סיבת דחייה" value={sub.rejectionReason} />
                  )}
                </div>
              </div>

              {sub.status === 'pending' && (
                <>
                  {rejectingId === sub.id ? (
                    <div style={s.rejectRow}>
                      <input
                        type="text"
                        placeholder="סיבת דחייה (אופציונלי)"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        style={s.rejectInput}
                      />
                      <button style={s.rejectConfirmBtn} onClick={() => reject(sub)} disabled={isBusy}>
                        {isBusy ? '...' : 'אישור דחייה'}
                      </button>
                      <button style={s.cancelBtn} onClick={() => { setRejectingId(null); setRejectReason(''); }} disabled={isBusy}>
                        ביטול
                      </button>
                    </div>
                  ) : (
                    <div style={s.actionRow}>
                      <button style={s.approveBtn} onClick={() => approve(sub)} disabled={isBusy}>
                        {isBusy ? 'מאשר...' : '✔ אישור והשלמת פרטים'}
                      </button>
                      <button style={s.rejectBtn} onClick={() => setRejectingId(sub.id)} disabled={isBusy}>
                        ✕ דחייה
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })
      )}

      <ImportBusinessesModal
        visible={importing}
        onClose={() => setImporting(false)}
        onImported={() => {
          showSuccess('הייבוא הושלם - השורות שהצליחו נוספו כהצעות ממתינות');
          if (statusFilter === 'pending') loadSubmissions();
          else setStatusFilter('pending');
        }}
      />
    </div>
  );
};

function DetailRow({ label, value, strong }) {
  return (
    <div style={{ ...s.detailRow, ...(strong ? s.detailRowStrong : {}) }}>
      <span style={s.detailLabel}>{label}:</span>
      <span style={{ ...s.detailValue, ...(strong ? s.detailValueStrong : {}) }}>{value}</span>
    </div>
  );
}

const s = {
  container: { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  title: { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  totalBadge: { background: '#e8eaf6', color: '#1f6feb', borderRadius: 12, padding: '4px 12px', fontSize: 13, fontWeight: 700 },
  exportBtn: { padding: '9px 16px', background: '#fff', color: '#1f6feb', border: '1.5px solid #c7d7fb', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  importBtn: { padding: '9px 16px', background: '#fff', color: '#1f6feb', border: '1.5px solid #c7d7fb', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  tabsRow: { display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' },
  tabBtn: { padding: '8px 16px', borderRadius: 999, border: '1.5px solid #e3e8ee', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#697386' },
  tabBtnActive: { background: '#1f6feb', borderColor: '#1f6feb', color: '#fff' },
  center: { padding: 40, textAlign: 'center', color: '#999' },
  errorBanner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },
  successBanner: { background: '#e8f5e9', color: '#2e7d32', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 },
  emptyState: { textAlign: 'center', padding: '60px 0' },

  card: { background: '#fff', border: '1.5px solid #e3e8ee', borderRadius: 14, padding: 16, marginBottom: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  cardHeaderRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
  statusBadge: { borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
  businessName: { flex: 1, fontSize: 15, fontWeight: 700, color: '#1a1f36' },
  dateText: { fontSize: 12, color: '#a3acb9' },
  cardBody: { display: 'flex', gap: 14 },
  thumb: { width: 90, height: 90, objectFit: 'cover', borderRadius: 10, border: '1px solid #e3e8ee', flexShrink: 0 },
  detailsCol: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4 },
  detailRow: { fontSize: 12.5 },
  detailRowStrong: { background: '#FEF9EE', borderRadius: 8, padding: '5px 8px', marginBottom: 2 },
  detailLabel: { color: '#8792a2', fontWeight: 700, marginLeft: 6 },
  detailValue: { color: '#4f566b', whiteSpace: 'pre-wrap' },
  detailValueStrong: { color: '#8C5A0C', fontWeight: 700 },

  actionRow: { display: 'flex', gap: 10, marginTop: 14 },
  approveBtn: { flex: 1, padding: '10px 16px', background: '#16A34A', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13.5, fontWeight: 700 },
  rejectBtn: { flex: 1, padding: '10px 16px', background: '#fff', color: '#c62828', border: '1.5px solid #fbc4c4', borderRadius: 10, cursor: 'pointer', fontSize: 13.5, fontWeight: 700 },
  rejectRow: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  rejectInput: { flex: 1, minWidth: 180, padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 13 },
  rejectConfirmBtn: { padding: '9px 16px', background: '#c62828', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  cancelBtn: { padding: '9px 16px', background: '#fff', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
};

export default BusinessSubmissionsTab;
